import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    // Snapshot the list first — splitting mutates the parent's children mid-iteration.
    Array.from(this.querySelectorAll('li')).forEach(this.#splitParagraphsInListItem);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * A multi_line_text_field metafield rendered via `metafield_tag` separates
   * lines with <br> (and wraps blocks in <p>). When inserted inside an <li>
   * in a richtext block, the result is one bullet with N visible lines. Split
   * it into one <li> per line so each line gets its own bullet.
   * @param {HTMLLIElement} li
   */
  #splitParagraphsInListItem(li) {
    const childNodes = Array.from(li.childNodes);

    const isBr = (node) => node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR';
    const isParagraph = (node) => node.nodeType === Node.ELEMENT_NODE && node.tagName === 'P';

    // Case 1: <li>line1<br>line2<br>line3</li> — split by <br>
    if (childNodes.some(isBr)) {
      const groups = [[]];
      for (const node of childNodes) {
        if (isBr(node)) {
          groups.push([]);
        } else {
          groups[groups.length - 1].push(node);
        }
      }
      this.#replaceListItemWithGroups(li, groups);
      return;
    }

    // Case 2: <li><p>line1</p><p>line2</p></li> — split each <p> into its own <li>
    const onlyParagraphs = childNodes.every((node) => {
      if (node.nodeType === Node.TEXT_NODE) return !node.textContent.trim();
      return isParagraph(node);
    });
    const paragraphs = childNodes.filter(isParagraph);
    if (onlyParagraphs && paragraphs.length >= 2) {
      const groups = paragraphs.map((p) => Array.from(p.childNodes));
      this.#replaceListItemWithGroups(li, groups);
    }
  }

  /**
   * Replaces a single <li> with one <li> per non-empty group of nodes.
   * @param {HTMLLIElement} li
   * @param {Node[][]} groups
   */
  #replaceListItemWithGroups(li, groups) {
    const parent = li.parentNode;
    if (!parent) return;

    const fragment = document.createDocumentFragment();
    let createdAny = false;
    for (const group of groups) {
      const hasContent = group.some((node) => {
        if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim();
        return true;
      });
      if (!hasContent) continue;
      const newLi = document.createElement('li');
      for (const node of group) newLi.appendChild(node);
      fragment.appendChild(newLi);
      createdAny = true;
    }
    if (createdAny) parent.replaceChild(fragment, li);
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
